import {
  Alert,
  Checkbox,
  Group,
  Paper,
  Select,
  Skeleton,
  Slider,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { KpiOutlierMode } from "@msc/shared";

import { hasRole, useCurrentUser } from "../../hooks/useAuth.js";
import { useKpiOutliers, useMyLeadSegments } from "../../hooks/useKpi.js";
import { useSegments } from "../../hooks/useSegments.js";
import { OutlierMatrix } from "../../components/kpi/OutlierMatrix.js";

/**
 * Ausreißer-Analyse (Excel „Ausreißeranalyse Kunden/Mitarbeiter"):
 * Monats-Umsatz-Heatmap je Kunde bzw. Berater über ein rollierendes
 * Monatsfenster; Zellen mit > ±Schwelle Abweichung vom 6-Monats-Median
 * werden geflaggt. Schwelle und Fensterbreite sind einstellbar.
 */
export function KpiAusreisserPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const { data: segments } = useSegments();
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [mode, setMode] = useState<KpiOutlierMode>("customer");
  // Zwoelf Monate als Vorgabe (Rueckmeldung Hendrik, 2026-08-20): Ein Jahr ist
  // der Zeitraum, ueber den hier tatsaechlich geurteilt wird. Die 24 Monate der
  // ersten Fassung stammten aus der Excel-Vorlage und machten die Matrix so
  // breit, dass die juengsten Monate — die einzigen, auf die man noch reagieren
  // kann — am rechten Rand standen. Der Regler geht weiterhin bis 36.
  const [months, setMonths] = useState(12);
  const [threshold, setThreshold] = useState(50);
  const [minDeviation, setMinDeviation] = useState(5000);
  const [nurEigene, setNurEigene] = useState(false);

  // Reichweite wie im Bereichs-Cockpit: Wer nur Service Lead ist, waehlt nur
  // unter seinen eigenen Bereichen. Die Grenze zieht der Server.
  const darfAlleBereiche = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
  ]);
  const { data: eigeneBereiche } = useMyLeadSegments(!darfAlleBereiche);
  // useMemo, damit die Vorbelegung unten nicht bei jedem Rendern neu laeuft:
  // Ein frisch erzeugtes Array ist fuer den Abhaengigkeitsvergleich immer neu.
  const waehlbar = useMemo(
    () =>
      darfAlleBereiche
        ? (segments ?? [])
        : (segments ?? []).filter((s) =>
            (eigeneBereiche ?? []).some((e) => e.segmentId === s.id),
          ),
    [darfAlleBereiche, segments, eigeneBereiche],
  );
  const istServiceLead = hasRole(user, "projektleiter");

  // Vorbelegung: eigenes Segment (BL/Stellv. bzw. Home-Bereich), sonst erstes.
  useEffect(() => {
    if (!segmentId && waehlbar.length > 0) {
      const preferred =
        waehlbar.find((s) => s.id === user?.defaultSegmentId) ?? waehlbar[0]!;
      setSegmentId(preferred.id);
    }
  }, [waehlbar, segmentId, user?.defaultSegmentId]);

  const { data, isLoading, isError } = useKpiOutliers(
    segmentId,
    mode,
    months,
    threshold / 100,
    minDeviation,
    nurEigene,
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={2}>{t("pages.kpi.outliers.title")}</Title>
          <Text c="dimmed" size="sm">
            {t("pages.kpi.outliers.subtitle")}
          </Text>
        </Stack>
        {waehlbar.length > 1 ? (
          <Select
            value={segmentId}
            data={waehlbar.map((s) => ({ value: s.id, label: s.name }))}
            onChange={setSegmentId}
            style={{ minWidth: 220 }}
          />
        ) : (
          <Text fw={500}>
            {waehlbar.find((s) => s.id === segmentId)?.name ?? ""}
          </Text>
        )}
      </Group>

      <Paper p="sm" withBorder>
        <Group gap="xl" align="flex-end">
          {/*
            Nur fuer Service Leads: Der Bereich enthaelt auch die Kunden der
            Kollegen. Wer seine eigene Liste durchgehen will, soll den Rest
            wegschalten koennen (Rueckmeldung Hendrik, 2026-08-20). Bewusst
            NICHT vorbelegt — die Ausreisser-Analyse ist eine Bereichsansicht,
            und wer sie oeffnet, soll zuerst den Bereich sehen.
          */}
          {istServiceLead && (
            <Checkbox
              size="xs"
              label={t("pages.kpi.outliers.nurEigene")}
              description={t("pages.kpi.outliers.nurEigeneHinweis")}
              checked={nurEigene}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNurEigene(e.currentTarget.checked)
              }
            />
          )}
          <Stack gap={4} style={{ minWidth: 220 }}>
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.kpi.outliers.monthsLabel", { count: months })}
            </Text>
            <Slider
              value={months}
              onChange={setMonths}
              min={12}
              max={36}
              step={6}
              marks={[12, 18, 24, 30, 36].map((v) => ({ value: v, label: String(v) }))}
            />
          </Stack>
          <Stack gap={4} style={{ minWidth: 220 }}>
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.kpi.outliers.thresholdLabel", { threshold })}
            </Text>
            <Slider
              value={threshold}
              onChange={setThreshold}
              min={10}
              max={150}
              step={5}
              marks={[25, 50, 100, 150].map((v) => ({ value: v, label: `${v}%` }))}
            />
          </Stack>
          <Stack gap={4} style={{ minWidth: 220 }}>
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.kpi.outliers.minDeviationLabel", {
                value: (minDeviation / 1000).toLocaleString("de-DE"),
              })}
            </Text>
            <Slider
              value={minDeviation}
              onChange={setMinDeviation}
              min={0}
              max={20000}
              step={1000}
              marks={[0, 5000, 10000, 20000].map((v) => ({
                value: v,
                label: `${v / 1000}k`,
              }))}
            />
          </Stack>
        </Group>
      </Paper>

      {isError && (
        <Alert color="red" title={t("common.error")}>
          {t("common.errorLoading")}
        </Alert>
      )}

      <Tabs
        value={mode}
        onChange={(v) => v && setMode(v as KpiOutlierMode)}
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="customer">
            {t("pages.kpi.outliers.tabCustomers")}
          </Tabs.Tab>
          <Tabs.Tab value="employee">
            {t("pages.kpi.outliers.tabEmployees")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={mode} pt="md">
          {isLoading || !data ? (
            <Skeleton height={360} />
          ) : (
            <OutlierMatrix data={data} />
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
